import React, { useState } from 'react';
import { Users, Clock, CheckCircle2, Plus, Phone, Mail } from 'lucide-react';

interface Member {
  id: string; initials: string; color: string;
  name: string; role: string; phone: string; email: string;
  horasSemanales: number; obras: string[]; estado: 'activo' | 'vacaciones' | 'baja';
}

const DEMO_TEAM: Member[] = [
  { id: '1', initials: 'DV', color: 'bg-orange-500', name: 'Diego Vázquez', role: 'Jefe de Obra', phone: '612 345 678', email: 'dvazquez@obra.es', horasSemanales: 45, obras: ['Construcción Chalet Goya', 'Reforma Oficinas'], estado: 'activo' },
  { id: '2', initials: 'PS', color: 'bg-purple-500', name: 'Paula Sánchez', role: 'Oficial 1ª', phone: '623 456 789', email: 'psanchez@obra.es', horasSemanales: 40, obras: ['Reforma Oficinas'], estado: 'activo' },
  { id: '3', initials: 'CR', color: 'bg-red-500', name: 'Carlos Ruiz', role: 'Peón', phone: '634 567 890', email: 'cruiz@obra.es', horasSemanales: 38, obras: ['Construcción Chalet Goya'], estado: 'activo' },
  { id: '4', initials: 'MG', color: 'bg-teal-500', name: 'María García', role: 'Encargada', phone: '645 678 901', email: 'mgarcia@obra.es', horasSemanales: 0, obras: [], estado: 'vacaciones' },
  { id: '5', initials: 'AL', color: 'bg-green-500', name: 'Andrés López', role: 'Subcontrata electricidad', phone: '656 789 012', email: 'alopez@electric.es', horasSemanales: 20, obras: ['Reforma Oficinas'], estado: 'activo' },
];

const estadoBadge: Record<string, string> = {
  activo: 'bg-green-500/20 text-green-400',
  vacaciones: 'bg-blue-500/20 text-blue-400',
  baja: 'bg-red-500/20 text-red-400',
};

export default function EquipoPage() {
  const [search, setSearch] = useState('');
  const filtered = DEMO_TEAM.filter(m =>
    m.name.toLowerCase().includes(search.toLowerCase()) ||
    m.role.toLowerCase().includes(search.toLowerCase())
  );
  const totalHoras = DEMO_TEAM.filter(m => m.estado === 'activo').reduce((s, m) => s + m.horasSemanales, 0);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Equipo</h1>
          <p className="text-sm text-gray-500 mt-0.5">{DEMO_TEAM.filter(m => m.estado === 'activo').length} activos · {totalHoras}h semanales</p>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-black rounded-xl font-semibold text-sm hover:bg-orange-400 transition-colors">
          <Plus size={16} /> Añadir miembro
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total equipo</div>
          <div className="text-3xl font-bold">{DEMO_TEAM.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Horas semana</div>
          <div className="text-3xl font-bold">{totalHoras}h</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Subcontratas</div>
          <div className="text-3xl font-bold">{DEMO_TEAM.filter(m => m.role.toLowerCase().includes('subcontrat')).length}</div>
        </div>
      </div>

      <div className="mb-4">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre o rol…"
          className="w-full max-w-sm text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-orange-300"
        />
      </div>

      <div className="space-y-3">
        {filtered.map(m => (
          <div key={m.id} className="bg-white border border-gray-200 rounded-2xl p-4 flex items-center gap-4 hover:border-orange-300 transition-colors">
            <div className={`w-11 h-11 rounded-full ${m.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>{m.initials}</div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-gray-900">{m.name}</div>
              <div className="text-sm text-gray-500">{m.role}</div>
            </div>
            <div className="hidden md:flex items-center gap-1 text-sm text-gray-400">
              <Clock size={14} /><span>{m.horasSemanales}h/sem</span>
            </div>
            <div className="hidden md:flex flex-wrap gap-1 max-w-xs">
              {m.obras.map((o, i) => (
                <span key={i} className="text-xs bg-orange-500/10 text-orange-400 px-2 py-0.5 rounded-full">{o.slice(0, 20)}{o.length > 20 ? '…' : ''}</span>
              ))}
            </div>
            <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${estadoBadge[m.estado]}`}>{m.estado}</span>
            <div className="flex items-center gap-2 text-gray-400">
              <a href={`tel:${m.phone}`} className="p-1.5 hover:text-orange-400 transition-colors"><Phone size={16} /></a>
              <a href={`mailto:${m.email}`} className="p-1.5 hover:text-orange-400 transition-colors"><Mail size={16} /></a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
